'use client';
import React, { useEffect } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { SITE_NAME } from '@/lib/site-config';

interface AppDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AppDownloadModal({ isOpen, onClose }: AppDownloadModalProps) {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        className="bg-card rounded-3xl p-8 max-w-sm w-full relative shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: 'slide-up 0.4s cubic-bezier(0.16,1,0.3,1) forwards' }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-xl bg-muted hover:bg-border transition-colors"
          aria-label="Close modal"
        >
          <Icon name="XMarkIcon" size={18} className="text-foreground" />
        </button>

        {/* Header */}
        <div className="flex flex-col items-center text-center mb-6">
          <div className="mb-4">
            <AppLogo size={56} />
          </div>
          <h2 className="font-heading font-bold text-xl text-foreground mb-2">
            Download {SITE_NAME}
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Apply to campaigns, submit reviews, and get paid — all in the app.
          </p>
        </div>

        {/* QR Code placeholder */}
        <div className="flex items-center justify-center mb-6">
          <div className="w-28 h-28 bg-muted rounded-2xl flex items-center justify-center border-2 border-dashed border-border">
            <div className="text-center">
              <Icon name="QrCodeIcon" size={40} className="text-muted-foreground mx-auto" />
              <span className="text-[10px] text-muted-foreground mt-1 block">Scan to download</span>
            </div>
          </div>
        </div>

        {/* Store Buttons */}
        <div className="space-y-3">
          <a
            href="#"
            className="flex items-center gap-3 bg-foreground text-background rounded-xl px-4 py-3 hover:opacity-90 transition-opacity"
          >
            <Icon name="DevicePhoneMobileIcon" size={22} className="text-background" />
            <div>
              <div className="text-[9px] text-background/60 uppercase tracking-wide">Get it on</div>
              <div className="text-sm font-semibold text-background font-heading">Google Play Store</div>
            </div>
          </a>
          <a
            href="#"
            className="flex items-center gap-3 bg-foreground text-background rounded-xl px-4 py-3 hover:opacity-90 transition-opacity"
          >
            <Icon name="DevicePhoneMobileIcon" size={22} className="text-background" />
            <div>
              <div className="text-[9px] text-background/60 uppercase tracking-wide">Download on the</div>
              <div className="text-sm font-semibold text-background font-heading">Apple App Store</div>
            </div>
          </a>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-4">
          Free to download · 50,000+ reviewers
        </p>
      </div>
    </div>
  );
}